import { stripNonDigits, upperName } from "./document-validation";

/**
 * O que a ficha de contato faz com `details` antes de gravar. Mora aqui porque
 * o novo lead também cria contato: sem a mesma limpeza, o índice único de CPF
 * (`idx_contacts_cpf_user_unique`, sobre os dígitos) deixa passar a mesma pessoa
 * digitada com e sem pontuação.
 */
export const normalizarDetalhesDoContato = (
  details: Record<string, unknown> | undefined,
): Record<string, unknown> => ({
  ...details,
  person_type: details?.person_type || "PF",
  cpf: stripNonDigits(String(details?.cpf ?? "")),
  name: upperName(String(details?.name ?? "")),
});

/**
 * O celular como o banco compara (migration 240, `normalizar_celular`): só
 * dígitos, e o 55 da frente sai quando sobram 12 ou 13. "(41) 99999-9999" e
 * "+55 41 999999999" são o mesmo número; 10 e 11 dígitos são números diferentes.
 */
export const normalizarCelular = (telefone: unknown): string => {
  const digitos = String(telefone ?? "").replace(/\D/g, "");
  return (digitos.length === 12 || digitos.length === 13) && digitos.startsWith("55")
    ? digitos.slice(2)
    : digitos;
};

export const MENSAGEM_SEM_CELULAR = "Informe o celular do contato.";
export const MENSAGEM_CELULAR_NA_SUA_BASE = "Este contato já está cadastrado na sua base.";
export const mensagemCelularDeOutro = (dono: string) => `Este contato já está cadastrado na base de ${dono}.`;

export type ErroDeCelular =
  | { tipo: "sem_celular" }
  | { tipo: "propria" }
  | { tipo: "outro"; dono: string };

/**
 * Lê a recusa do gatilho `trg_contatos_celular_unico` (migration 240). O
 * PostgREST devolve o HINT/DETAIL do Postgres no corpo; o ra-core guarda o
 * corpo em `error.body`. Sem o corpo (outro caminho), a frase do banco basta.
 */
export const erroDeCelular = (error: unknown): ErroDeCelular | null => {
  const e = (error ?? {}) as { message?: string; hint?: string; details?: string; body?: Record<string, unknown> };
  const corpo = (e.body ?? {}) as { hint?: string; details?: string; message?: string };
  const dica = String(corpo.hint ?? e.hint ?? "");
  const mensagem = String(corpo.message ?? e.message ?? "");
  if (dica === "CONTATO_SEM_CELULAR" || mensagem === MENSAGEM_SEM_CELULAR) return { tipo: "sem_celular" };
  if (dica === "CONTATO_DUPLICADO_PROPRIO" || mensagem === MENSAGEM_CELULAR_NA_SUA_BASE) return { tipo: "propria" };
  const deOutro = /^Este contato já está cadastrado na base de (.+)\.$/.exec(mensagem);
  if (dica === "CONTATO_DUPLICADO_OUTRO" || deOutro) {
    return { tipo: "outro", dono: String(corpo.details ?? e.details ?? deOutro?.[1] ?? "outra pessoa") };
  }
  return null;
};

export const mensagemDeErroAoGravarContato = (error: unknown): string => {
  const celular = erroDeCelular(error);
  if (celular?.tipo === "sem_celular") return MENSAGEM_SEM_CELULAR;
  if (celular?.tipo === "propria") return MENSAGEM_CELULAR_NA_SUA_BASE;
  if (celular?.tipo === "outro") return mensagemCelularDeOutro(celular.dono);
  const message = (error as { message?: string } | null)?.message ?? "";
  if (/idx_contacts_cpf_user_unique|duplicate key/i.test(message)) {
    return "Já existe um contato com este CPF/CNPJ na sua base.";
  }
  return message || "Não foi possível criar o contato.";
};
